import React, {useContext, useState, useEffect, useMemo, useRef} from 'react';
import { trackMetaEvent, retrievePendingPurchase } from '../../../utils/metaPixel';
import AppContext from "../../../context";

import Product from "./Product";
import Loading from "../../components/Loading";

import Data from "../../../repository";

import styles from '../../../styles/Tokens.module.scss';
import RedeemVoucher from "./RedeemVoucher";
import {NavLink, useLocation, useSearchParams} from "react-router-dom";
import moment from "moment";

function List() {
	const context = useContext(AppContext);
	const { search } = useLocation();
	const [searchParams] = useSearchParams();

	const [loading, setLoading] = useState(true);
	const [products, setProducts] = useState([]);
	const [creditSubscriptions, setCreditSubscriptions] = useState([]);
	const [actionLoading, setActionLoading] = useState(false);
	const [verifying, setVerifying] = useState(false);
	const [verificationMessage, setVerificationMessage] = useState(null);

	const isWorldpay = context.domain?.paymentProcessor === 'Worldpay';
	const isSumup = context.domain?.paymentProcessor === 'SumUp';
	const isHybridNew = context.domain?.businessType === 'hybrid-new';

	useEffect(() => {
		const init = async () => {
			setProducts(await fetchProducts());
			if (isWorldpay || isSumup) {
				await loadCreditSubscriptions();
			}

			// Verify payment if returning from successful payment
			if ((isWorldpay || isSumup) && searchParams.get('paymentStatus') === 'success') {
				const transactionId = searchParams.get('transactionId');
				const checkoutId = searchParams.get('checkoutId');
				const setupId = searchParams.get('setupId');

				if (transactionId) {
					await verifyPayment(transactionId, checkoutId);
				} else if (setupId) {
					await verifySubscription(setupId);
				}
			}

			setLoading(false);
		};
		init();
	}, []);

	const fireTokensPurchase = () => {
		const pixelId = context.domain?.metaPixelEnabled ? context.domain?.metaPixelId : null;
		if (!pixelId) return;
		const pending = retrievePendingPurchase('tokens');
		trackMetaEvent({
			pixelId,
			eventName: 'Purchase',
			params: {
				content_type: 'tokens',
				content_ids: pending?.productId ? [pending.productId] : undefined,
				content_name: pending?.name,
				value: pending?.value || 0,
				currency: pending?.currency || context.domain?.metaCurrency || 'GBP'
			}
		});
	};

	const verifyPayment = async (transactionId, checkoutId) => {
		setVerifying(true);
		try {
			const result = isSumup
				? await Data.verifySumupPayment(transactionId, checkoutId)
				: await Data.verifyWorldpayPayment(transactionId);
			const credits = result.data?.credits ?? 0;
			setVerificationMessage(
				credits > 0
					? `Payment verified! ${credits} tokens added to your account.`
					: 'Payment verified!'
			);
			fireTokensPurchase();
			await context.fetchUser(); // Refresh user to update balance
		} catch (err) {
			console.error('Payment verification failed:', err);
			setVerificationMessage('Payment verification pending. Your tokens will be added shortly.');
		}
		setVerifying(false);
	};

	const verifySubscription = async (setupId) => {
		setVerifying(true);
		try {
			const result = isSumup
				? await Data.verifySumupSubscription(setupId)
				: await Data.verifyWorldpaySubscription(setupId);
			setVerificationMessage('Subscription activated successfully!');
			fireTokensPurchase();
			await loadCreditSubscriptions();
			await context.fetchUser(); // Refresh user to update balance
		} catch (err) {
			console.error('Subscription verification failed:', err);
			if (err.response?.status === 404) {
				// Setup already processed (by webhook) — still fire Purchase if pending
				fireTokensPurchase();
				setVerificationMessage('Subscription activated!');
			} else {
				setVerificationMessage('Subscription activation pending. Please refresh in a moment.');
			}
		}
		setVerifying(false);
	};

	const fetchProducts = async () => {
		const result = await Data.fetchPublicProducts();
		return result.data;
	}

	const loadCreditSubscriptions = async () => {
		try {
			const result = isSumup
				? await Data.fetchSumupSubscriptions()
				: await Data.fetchWorldpaySubscriptions();
			const creditSubs = result.data.filter(sub =>
				sub.type === 'credits' &&
				['active', 'paused', 'past_due', 'pending_cancellation'].includes(sub.status)
			);
			setCreditSubscriptions(creditSubs);
		} catch (err) {
			console.error('Failed to load subscriptions:', err);
		}
	}

	const handlePauseSubscription = async (subscriptionId) => {
		setActionLoading(true);
		try {
			if (isSumup) {
				await Data.pauseSumupSubscription(subscriptionId);
			} else {
				await Data.pauseWorldpaySubscription(subscriptionId);
			}
			await loadCreditSubscriptions();
		} catch (err) {
			alert('Failed to pause subscription. Please try again.');
		}
		setActionLoading(false);
	}

	const handleResumeSubscription = async (subscriptionId) => {
		setActionLoading(true);
		try {
			if (isSumup) {
				await Data.resumeSumupSubscription(subscriptionId);
			} else {
				await Data.resumeWorldpaySubscription(subscriptionId);
			}
			await loadCreditSubscriptions();
		} catch (err) {
			alert('Failed to resume subscription. Please try again.');
		}
		setActionLoading(false);
	}

	const handleCancelSubscription = async (subscriptionId) => {
		if (!window.confirm('Are you sure you want to cancel this subscription?')) return;
		setActionLoading(true);
		try {
			if (isSumup) {
				await Data.cancelSumupSubscription(subscriptionId, false);
			} else {
				await Data.cancelWorldpaySubscription(subscriptionId, false);
			}
			await loadCreditSubscriptions();
		} catch (err) {
			alert('Failed to cancel subscription. Please try again.');
		}
		setActionLoading(false);
	}

	if(loading) return <Loading button={false} />

	return (
		<>
			{search.includes('paymentStatus') ?
				<div className={styles.container}>
					{verifying ? (
						<p className={styles.success}>Verifying your payment...</p>
					) : verificationMessage ? (
						<p className={styles.success}>{verificationMessage}</p>
					) : (
						<>
							{search.includes('success') ? <p className={styles.success}>Your payment was successful!</p> : null}
							{search.includes('pending') ? <p className={styles.notSuccess}>Your payment is pending, please check back soon.</p> : null}
							{search.includes('failed') ? <p className={styles.notSuccess}>Your payment did not complete, please try again.</p> : null}
						</>
					)}
				</div>
			: null}

			<section className={styles.balance}>
				<h2>Balance</h2>
				{isHybridNew ? (
					<>
						<p>{context.user.openGymBalance || 0} open gym token{Number(context.user.openGymBalance || 0) === 1 ? '' : 's'}</p>
						<p>{context.user.classBalance || 0} class token{Number(context.user.classBalance || 0) === 1 ? '' : 's'}</p>
					</>
				) : (
					<p>{context.user.balance} token{context.user.balance === 1 ? '' : 's'}</p>
				)}
			</section>

			{(isWorldpay || isSumup) && creditSubscriptions.length > 0 && (
				<section className={styles.subscriptions}>
					<h2>Active Subscriptions</h2>
					{creditSubscriptions.map((sub) => (
						<div key={sub._id} className={styles.subscription}>
							<div className={styles.subscriptionInfo}>
								<p><strong>{sub.Product?.credits || '?'} Tokens</strong> - £{(sub.amount / 100).toFixed(2)} / {sub.intervalCount > 1 ? `${sub.intervalCount} ${sub.interval}s` : sub.interval}</p>
								<p className={styles.subscriptionStatus}>
									Status: <span className={(sub.status === 'active' && !sub.cancelAtPeriodEnd) ? styles.active : sub.status === 'paused' ? styles.paused : sub.status === 'past_due' ? styles.past_due : styles.pending_cancellation}>
										{(sub.status === 'active' && sub.cancelAtPeriodEnd) ? 'pending cancellation' : sub.status.replace('_', ' ')}
									</span>
									{sub.status === 'active' && !sub.cancelAtPeriodEnd && sub.nextBillingDate && (
										<> - Next billing: {moment(sub.nextBillingDate).format('Do MMM YYYY')}</>
									)}
									{((sub.status === 'pending_cancellation') || (sub.status === 'active' && sub.cancelAtPeriodEnd)) && sub.currentPeriodEnd && (
										<> - Ends: {moment(sub.currentPeriodEnd).format('Do MMM YYYY')}</>
									)}
								</p>
							</div>
							<div className={styles.subscriptionActions}>
								{sub.status === 'active' && !sub.cancelAtPeriodEnd && (
									<>
										<button
											className="btn btnEmpty btnSmall"
											onClick={() => handlePauseSubscription(sub._id)}
											disabled={actionLoading}
										>
											Pause
										</button>
										<button
											className="btn btnEmpty btnSmall btnDanger"
											onClick={() => handleCancelSubscription(sub._id)}
											disabled={actionLoading}
										>
											Cancel
										</button>
									</>
								)}
								{sub.status === 'paused' && (
									<button
										className="btn btnPrimary btnSmall"
										onClick={() => handleResumeSubscription(sub._id)}
										disabled={actionLoading}
									>
										Resume
									</button>
								)}
							</div>
						</div>
					))}
				</section>
			)}

			<section>
				<h2>Purchase Tokens</h2>
				<div className={styles.products}>
					{!products.length && <div className={styles.none}><p>There aren't any products available for purchase, please try again later.</p></div>}

					{products.map((product) => {
						return <Product {...product} />
					})}
				</div>
			</section>

			<section>
				<h2>Redeem Gift Voucher</h2>
				<RedeemVoucher />
			</section>

			<NavLink to={'/gift-vouchers'} className={`btn btnPrimary linkBlock`}>Purchase Gift Vouchers</NavLink>
		</>
	);
}

export default List;
